import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

export type Language = "vi" | "en";

type LanguageContextValue = {
  language: Language;
  setLanguage: (language: Language) => void;
  toggleLanguage: () => void;
};

const languageStorageKey = "human-note.language";
const LanguageContext = createContext<LanguageContextValue | null>(null);

type TranslationEntry = {
  vi: string;
  en: string;
  variants?: string[];
};

const entries: TranslationEntry[] = [
  { vi: "Human Note", en: "Human Note" },
  { vi: "Human Note Books", en: "Human Note Books" },
  { vi: "Ghi nhanh, sắp xếp, chắt lọc, liên kết", en: "Capture, organize, refine, connect", variants: ["Ghi nhanh, sáº¯p xáº¿p, cháº¯t lá»c, liÃªn káº¿t"] },
  { vi: "Nạp cây sách, sửa mục lục, ghi note theo từng mục", en: "Import book trees, edit tables of contents, write notes by section", variants: ["Nap cay sach, sua muc luc, ghi note theo tung muc"] },
  { vi: "Private owner workspace", en: "Private owner workspace" },
  { vi: "Owner passcode", en: "Owner passcode" },
  { vi: "Enter passcode", en: "Enter passcode" },
  { vi: "Sign in", en: "Sign in" },
  { vi: "Signing in", en: "Signing in" },
  { vi: "Đăng xuất", en: "Sign out" },
  { vi: "Đang tải Human Note", en: "Loading Human Note", variants: ["Loading Human Note"] },
  { vi: "Đang tải Books", en: "Loading Books", variants: ["Loading Books"] },

  { vi: "Hộp thư", en: "Inbox", variants: ["Inbox"] },
  { vi: "Bản đồ sách", en: "Book Map", variants: ["Book Map"] },
  { vi: "Thư viện", en: "Library", variants: ["Library"] },
  { vi: "Ontology", en: "Ontology" },
  { vi: "Network", en: "Network" },
  { vi: "Hướng dẫn", en: "Guide", variants: ["Guide"] },
  { vi: "Main", en: "Main" },
  { vi: "Tìm note, nguồn học, task, concept", en: "Search notes, sources, tasks, concepts", variants: ["TÃ¬m note, nguá»“n há»c, task, concept"] },

  { vi: "Nguồn học", en: "Learning sources", variants: ["Nguá»“n há»c"] },
  { vi: "Course, book, article, topic", en: "Course, book, article, topic" },
  { vi: "Tạo nguồn học", en: "Create source", variants: ["Táº¡o nguá»“n há»c"] },
  { vi: "Tên course, module, bài viết", en: "Course, module, or article title", variants: ["TÃªn course, module, bÃ i viáº¿t"] },
  { vi: "Không có parent", en: "No parent", variants: ["KhÃ´ng cÃ³ parent"] },
  { vi: "Đang lưu", en: "Saving", variants: ["Äang lÆ°u"] },
  { vi: "Lưu", en: "Save", variants: ["LÆ°u"] },
  { vi: "Lưu trữ", en: "Archive", variants: ["LÆ°u trá»¯"] },
  { vi: "Link tham khảo của nguồn học", en: "Source reference links", variants: ["Link tham kháº£o cá»§a nguá»“n há»c"] },

  { vi: "Ghi nhanh", en: "Quick capture" },
  { vi: "Chọn nguồn học để gắn ngữ cảnh", en: "Choose a source for context", variants: ["Chá»n nguá»“n há»c Ä‘á»ƒ gáº¯n ngá»¯ cáº£nh"] },
  { vi: "Ghi thẳng điều vừa học. Cấu trúc, thuộc tính, link và liên kết có thể bổ sung sau.", en: "Write what you just learned. Structure, properties, links, and relations can be added later.", variants: ["Ghi tháº³ng Ä‘iá»u vá»«a há»c. Cáº¥u trÃºc, thuá»™c tÃ­nh, link vÃ  liÃªn káº¿t cÃ³ thá»ƒ bá»• sung sau."] },
  { vi: "Gắn relation", en: "Add relation", variants: ["Gáº¯n relation"] },
  { vi: "Gợi ý thuộc tính", en: "Property hints", variants: ["Gá»£i Ã½ thuá»™c tÃ­nh"] },
  { vi: "Lưu note", en: "Save note", variants: ["LÆ°u note"] },
  { vi: "Relation khi ghi note", en: "Relation while capturing" },
  { vi: "Chọn target sau", en: "Choose target later", variants: ["Chá»n target sau"] },
  { vi: "Kết quả tìm kiếm", en: "Search results", variants: ["Káº¿t quáº£ tÃ¬m kiáº¿m"] },
  { vi: "AI review queue", en: "AI review queue" },
  { vi: "Gợi ý sẽ nằm chờ cho đến khi bạn duyệt hoặc bỏ qua", en: "Suggestions wait here until you accept or reject them", variants: ["Gá»£i Ã½ sáº½ náº±m chá» cho Ä‘áº¿n khi báº¡n duyá»‡t hoáº·c bá» qua"] },
  { vi: "Chưa có gợi ý nào. Bấm Suggest trên một note để tạo gợi ý mới.", en: "No suggestions yet. Click Suggest on a note to create suggestions.", variants: ["ChÆ°a cÃ³ gá»£i Ã½ nÃ o. Báº¥m Suggest trÃªn má»™t note Ä‘á»ƒ táº¡o gá»£i Ã½ má»›i."] },
  { vi: "Đang gợi ý", en: "Suggesting", variants: ["Äang gá»£i Ã½"] },
  { vi: "Note đang hoạt động", en: "Active notes", variants: ["Note Ä‘ang hoáº¡t Ä‘á»™ng"] },
  { vi: "Files đính kèm", en: "Attached files", variants: ["Files Ä‘Ã­nh kÃ¨m"] },
  { vi: "Đang tải", en: "Uploading", variants: ["Äang táº£i"] },
  { vi: "Chưa có file nào.", en: "No files yet.", variants: ["ChÆ°a cÃ³ file nÃ o."] },
  { vi: "Xóa", en: "Delete", variants: ["XÃ³a"] },
  { vi: "Lưu relation", en: "Save relation", variants: ["LÆ°u relation"] },

  { vi: "Tạo book", en: "Create book", variants: ["Tao book"] },
  { vi: "Tạo book đầu tiên để nạp cây thư mục sách.", en: "Create the first book to import its folder tree.", variants: ["Tao book dau tien de nap cay thu muc sach."] },
  { vi: "Nạp thư mục", en: "Import folder", variants: ["Nap thu muc"] },
  { vi: "Mục lục", en: "Table of contents", variants: ["Muc luc"] },
  { vi: "Thêm mục", en: "Add item", variants: ["Them muc"] },
  { vi: "Lưu mục lục", en: "Save table of contents", variants: ["Luu muc luc"] },
  { vi: "Xương sống", en: "Outline", variants: ["Xuong song"] },
  { vi: "Loại", en: "Type", variants: ["Loai"] },
  { vi: "Nút", en: "Actions", variants: ["Nut"] },
  { vi: "Trước", en: "Previous", variants: ["Truoc"] },
  { vi: "Sau", en: "Next", variants: ["Sau"] },
  { vi: "Mục con", en: "Child item", variants: ["Muc con"] },
  { vi: "Mục ngang", en: "Sibling item", variants: ["Muc ngang"] },
  { vi: "Note hiện tại", en: "Current note", variants: ["Note hien tai"] },
  { vi: "Ghi note riêng cho mục này", en: "Write a note for this item", variants: ["Ghi note rieng cho muc nay"] },
  { vi: "Lịch sử note của sách", en: "Book note history", variants: ["Lich su note cua sach"] },
  { vi: "Chưa có note nào trong cây sách này.", en: "No notes in this book tree yet.", variants: ["Chua co note nao trong cay sach nay."] },
  { vi: "Không rõ mục", en: "Unknown item", variants: ["Khong ro muc"] },
  { vi: "Tab note của mục đang chọn", en: "Note tab for the selected item", variants: ["Tab note cua muc dang chon"] },
  { vi: "mục sẽ được tạo", en: "items will be created", variants: ["muc se duoc tao"] },
  { vi: "Thông tin / thuộc tính", en: "Info / properties" },
  { vi: "Thêm", en: "Add" },
  { vi: "Tên thuộc tính", en: "Property name" },
  { vi: "Giá trị", en: "Value" },
  { vi: "Giá trị thuộc tính", en: "Property value" },
  { vi: "Xóa thuộc tính", en: "Delete property" },
  { vi: "Lưu thuộc tính", en: "Save properties" },
  { vi: "Mối quan hệ của mục", en: "Item relationships" },
  { vi: "Loại quan hệ", en: "Relationship type" },
  { vi: "Đối tượng liên quan", en: "Related object" },
  { vi: "Chọn mục/note liên quan", en: "Choose related item/note" },
  { vi: "Thuộc tính quan hệ", en: "Relationship properties" },
  { vi: "Tên thuộc tính quan hệ", en: "Relationship property name" },
  { vi: "Giá trị thuộc tính quan hệ", en: "Relationship property value" },
  { vi: "Xóa thuộc tính quan hệ", en: "Delete relationship property" },
  { vi: "Lưu quan hệ", en: "Save relationship" },
  { vi: "Thuộc tính note", en: "Note properties" },
  { vi: "Tên thuộc tính note", en: "Note property name" },
  { vi: "Giá trị thuộc tính note", en: "Note property value" },
  { vi: "Xóa thuộc tính note", en: "Delete note property" },
  { vi: "Link của note", en: "Note links" },
  { vi: "URL note", en: "Note URL" },
  { vi: "Tên link note", en: "Note link name" },
  { vi: "Xóa link note", en: "Delete note link" },
  { vi: "Mối quan hệ của note", en: "Note relationship" },
  { vi: "Loại quan hệ note", en: "Note relationship type" },
  { vi: "Đối tượng liên quan với note", en: "Object related to note" },
  { vi: "Không tạo thêm relation", en: "Do not create extra relation" },
  { vi: "Tên thuộc tính quan hệ note", en: "Note relationship property name" },
  { vi: "Giá trị thuộc tính quan hệ note", en: "Note relationship property value" },
  { vi: "Xóa thuộc tính quan hệ note", en: "Delete note relationship property" },

  { vi: "Thư viện học tập", en: "Learning library", variants: ["ThÆ° viá»‡n há»c táº­p"] },
  { vi: "Tất cả nguồn học và note đã lưu", en: "All saved sources and notes", variants: ["Táº¥t cáº£ nguá»“n há»c vÃ  note Ä‘Ã£ lÆ°u"] },
  { vi: "Mở Inbox", en: "Open Inbox", variants: ["Má»Ÿ Inbox"] },
  { vi: "Nguồn học theo loại", en: "Sources by type", variants: ["Nguá»“n há»c theo loáº¡i"] },
  { vi: "Tiến độ TB", en: "Avg progress", variants: ["Tiáº¿n Ä‘á»™ TB"] },
  { vi: "Đang học nhiều", en: "Most studied", variants: ["Äang há»c nhiá»u"] },
  { vi: "Tiếp tục capture", en: "Continue capturing", variants: ["Tiáº¿p tá»¥c capture"] },
  { vi: "Quay về Inbox", en: "Back to Inbox", variants: ["Quay vá» Inbox"] },
  { vi: "Note gần đây", en: "Recent notes", variants: ["Note gáº§n Ä‘Ã¢y"] },

  { vi: "Khu vườn tri thức", en: "Knowledge garden", variants: ["Khu vÆ°á»n tri thá»©c"] },
  { vi: "Sức khỏe mạng lưới học tập", en: "Learning network health", variants: ["Sá»©c khá»e máº¡ng lÆ°á»›i há»c táº­p"] },
  { vi: "Graph cục bộ", en: "Local graph", variants: ["Graph cá»¥c bá»™"] },
  { vi: "Gợi ý làm giàu", en: "Enrichment suggestions", variants: ["Gá»£i Ã½ lÃ m giÃ u"] },
  { vi: "Mở review queue", en: "Open review queue", variants: ["Má»Ÿ review queue"] },

  { vi: "Hướng dẫn cho người mới", en: "Beginner guide", variants: ["HÆ°á»›ng dáº«n cho ngÆ°á»i má»›i"] },
  { vi: "Bắt đầu ghi note", en: "Start capturing", variants: ["Báº¯t Ä‘áº§u ghi note"] },
  { vi: "Một ngày sử dụng đơn giản", en: "A simple day of use", variants: ["Má»™t ngÃ y sá»­ dá»¥ng Ä‘Æ¡n giáº£n"] },
  { vi: "Nên chọn loại nào?", en: "Which type should I choose?", variants: ["NÃªn chá»n loáº¡i nÃ o?"] },
  { vi: "Ví dụ gắn với cách học thật", en: "Examples from real study workflows", variants: ["VÃ­ dá»¥ gáº¯n vá»›i cÃ¡ch há»c tháº­t"] },

  { vi: "Tổng quan các note, nguồn và liên kết đang có", en: "Overview of current notes, sources, and links", variants: ["Tá»•ng quan cÃ¡c note, nguá»“n vÃ  liÃªn káº¿t Ä‘ang cÃ³"] },
  { vi: "Relation", en: "Relation" },
  { vi: "Cạnh đang hiển thị", en: "Visible edges", variants: ["Cáº¡nh Ä‘ang hiá»ƒn thá»‹"] },
  { vi: "Note đã nối", en: "Connected notes", variants: ["Note Ä‘Ã£ ná»‘i"] },
  { vi: "Cần chăm sóc", en: "Needs care", variants: ["Cáº§n chÄƒm sÃ³c"] },
  { vi: "Relations", en: "Relations" },
  { vi: "Điểm sáng", en: "Highlights", variants: ["Äiá»ƒm sÃ¡ng"] },
  { vi: "Cần làm giàu", en: "Needs enrichment", variants: ["Cáº§n lÃ m giÃ u"] },
  { vi: "Mở Inbox để Connect", en: "Open Inbox to connect", variants: ["Má»Ÿ Inbox Ä‘á»ƒ Connect"] },

  { vi: "Cách Human Note sắp xếp tri thức của bạn", en: "How Human Note organizes your knowledge", variants: ["CÃ¡ch Human Note sáº¯p xáº¿p tri thá»©c cá»§a báº¡n"] },
  { vi: "Mở Guide", en: "Open Guide", variants: ["Má»Ÿ Guide"] },
  { vi: "Sources", en: "Sources" },
  { vi: "Notes", en: "Notes" },
  { vi: "Bản đồ đơn giản", en: "Simple map", variants: ["Báº£n Ä‘á»“ Ä‘Æ¡n giáº£n"] },
  { vi: "Loại note hiện có", en: "Current note types", variants: ["Loáº¡i note hiá»‡n cÃ³"] },
  { vi: "Relation nên dùng", en: "Recommended relations", variants: ["Relation nÃªn dÃ¹ng"] },
  { vi: "Note có thể cần phân loại thêm", en: "Notes that may need more classification", variants: ["Note cÃ³ thá»ƒ cáº§n phÃ¢n loáº¡i thÃªm"] },
  { vi: "Cách dùng trong 2 phút", en: "How to use it in 2 minutes", variants: ["CÃ¡ch dÃ¹ng trong 2 phÃºt"] },
  { vi: "Vào Inbox", en: "Go to Inbox", variants: ["VÃ o Inbox"] },

  { vi: "Link tham khảo", en: "Reference links", variants: ["Link tham kháº£o"] },
  { vi: "Tải lại", en: "Reload", variants: ["Táº£i láº¡i"] },
  { vi: "Tên link", en: "Link name", variants: ["TÃªn link"] },
  { vi: "Lưu link", en: "Save link", variants: ["LÆ°u link"] },

  // IA rework nav labels
  { vi: "Ghi chép", en: "Capture" },
  { vi: "Phát triển", en: "Grow" },

  // RM-E garden tab + panel (bilingual, follows the language toggle)
  { vi: "Vườn", en: "Garden", variants: ["VÆ°á»n"] },
  { vi: "Vườn của bạn", en: "Your garden", variants: ["VÆ°á»n cá»§a báº¡n"] },
  { vi: "Vườn lớn dần theo việc bạn học — ghi note, tập trung, kết nối ý.", en: "Your garden grows as you learn — capture notes, focus, connect ideas." },
  { vi: "ngày streak", en: "day streak" },
  { vi: "Bắt đầu chuỗi hôm nay", en: "Start a streak today" },
  { vi: "Điểm vườn", en: "Garden points" },
  { vi: "Phiên tập trung", en: "Focus sessions" }
];

const byVariant = new Map<string, TranslationEntry>();

for (const entry of entries) {
  [entry.vi, entry.en, ...(entry.variants ?? [])].forEach((variant) => byVariant.set(variant, entry));
}

function getInitialLanguage(): Language {
  if (typeof window === "undefined") {
    return "vi";
  }

  return window.localStorage.getItem(languageStorageKey) === "en" ? "en" : "vi";
}

function translateText(value: string, language: Language) {
  const prefix = value.match(/^\s*/)?.[0] ?? "";
  const suffix = value.match(/\s*$/)?.[0] ?? "";
  const trimmed = value.trim();
  const entry = byVariant.get(trimmed);

  if (!entry) {
    return value;
  }

  return `${prefix}${entry[language]}${suffix}`;
}

function translateElement(root: ParentNode, language: Language) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode as Text);
  }

  textNodes.forEach((node) => {
    const next = translateText(node.nodeValue ?? "", language);
    if (next !== node.nodeValue) {
      node.nodeValue = next;
    }
  });

  root.querySelectorAll?.("[placeholder], [title], [aria-label]").forEach((element) => {
    ["placeholder", "title", "aria-label"].forEach((attribute) => {
      const value = element.getAttribute(attribute);
      if (!value) {
        return;
      }

      const next = translateText(value, language);
      if (next !== value) {
        element.setAttribute(attribute, next);
      }
    });
  });
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => getInitialLanguage());

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      setLanguage: (nextLanguage) => {
        setLanguageState(nextLanguage);
        window.localStorage.setItem(languageStorageKey, nextLanguage);
      },
      toggleLanguage: () => {
        const nextLanguage = language === "vi" ? "en" : "vi";
        setLanguageState(nextLanguage);
        window.localStorage.setItem(languageStorageKey, nextLanguage);
      }
    }),
    [language]
  );

  useEffect(() => {
    document.documentElement.lang = language;
    translateElement(document.body, language);

    const observer = new MutationObserver(() => translateElement(document.body, language));
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const value = useContext(LanguageContext);

  if (!value) {
    throw new Error("useLanguage must be used inside LanguageProvider");
  }

  return value;
}

export function tr(value: string, language: Language) {
  return translateText(value, language).trim();
}
